import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import CustomTable from '../../components/CustomTable'
import ColumnSelector from '../../components/ColumnSelector'
import { buildCsvBlob, downloadBlob, escapeCsvCell, CSV_CONFIRM_THRESHOLD } from '../../utils/csv'
import { parseLabelUnit, headerLabel } from '../../utils/columns'
import Api from '../../api/endPoints'
import { mapDeviceHistoryToRows } from '../../api/deviceHistoryMapper'
import { computeQuickRangeBounds } from '../../utils/timeRange'
import { useDeviceDashboardSocket } from '../../socket/deviceDashboardSocket'
import { useTelemetryHistory, mergeIntoWindow } from '../../socket/useTelemetryHistory'
import { setVimDevices, selectVimDeviceById } from '../../features/vimData/vimSlice'
import { setBreadcrumb } from '../../features/breadcrumb/breadcrumbSlice'
import TimeRangeDropdown from './TimeRangeDropdown'

const onOffRender = (key) => (row) => {
  const value = row[key]
  if (value === undefined) return undefined
  return <span style={{ color: value === 'ON' ? '#0ca30c' : '#616a79' }}>{value}</span>
}


const UNIT_BY_KEY = {
  'deviceInfo.upTime': 's',
  'deviceInfo.iotVolt': 'V',
  'deviceInfo.chipTemp': '°C',
  'deviceInfo.ramUsed': 'kB',
  'locationData.gps[3]': 'km/h',
  'systemData.odoMeter': 'km',
  'metricsData.supplyMinValue': 'V',
}


const TIMESTAMP_COLUMN = { key: 'timestamp', label: 'Timestamp', width: '184px', sortable: true }


const SUPPORTED_API_KEYS = new Set([
  'deviceInfo.upTime', 'deviceInfo.iotVolt', 'deviceInfo.chipTemp', 'deviceInfo.ramUsed',
  'deviceInfo.simNo', 'deviceInfo.aFlashSize',
  'networkData.nwOperName',
  'networkData.nwStrength[0]', 'networkData.nwStrength[1]', 'networkData.nwStrength[2]', 'networkData.nwStrength[3]',
  'locationData.gps[0]', 'locationData.gps[1]', 'locationData.gps[2]', 'locationData.gps[3]', 'locationData.gps[4]',
  'locationData.gps[5]', 'locationData.gps[6]', 'locationData.gps[7]', 'locationData.gps[8]', 'locationData.gps[9]',
  'locationData.gps[10]', 'locationData.gps[11]', 'locationData.gps[12]', 'locationData.gps[13]',
  'systemData.activityStatus', 'systemData.tripStatus', 'systemData.psmStatus', 'systemData.efficiencyMode',
  'systemData.immobStatus', 'systemData.odoMeter', 'systemData.tripMeter',
  'systemData.connectedSrvr[1]', 'systemData.connectedSrvr[2]',
  'metricsData.bootCnt', 'metricsData.uartErr', 'metricsData.canErr', 'metricsData.mqttErrCnt',
  'metricsData.nwJamStatus', 'metricsData.rwErrCnt', 'metricsData.selfRstCnt', 'metricsData.simErrCnt',
  'metricsData.supplyMinValue', 'metricsData.supplyUvCnt', 'metricsData.totDataCnsmd', 'metricsData.tripDistance',
  'metricsData.assertFailCnt', 'metricsData.gpsErrCount', 'metricsData.gpsOutSec', 'metricsData.intrntErrCnt',
])


const ON_OFF_API_KEYS = new Set([
  'systemData.activityStatus', 'systemData.tripStatus', 'systemData.psmStatus',
  'systemData.efficiencyMode', 'systemData.immobStatus', 'metricsData.nwJamStatus',
])


const HistoricalData = () => {
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [viewMode, setViewMode] = useState('list')
  const [sortDir, setSortDir] = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [timeRange, setTimeRange] = useState(() => ({ label: 'Last 30 min - Realtime', ...computeQuickRangeBounds('Last 30 min - Realtime') }))
  const [deviceHistory, setDeviceHistory] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  // Bumped by the error state's Retry button to re-run the fetch effect.
  const [reloadKey, setReloadKey] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [apiColumns, setApiColumns] = useState([])
  const [visibleKeys, setVisibleKeys] = useState([])

  const vimDevice = useSelector((state) => selectVimDeviceById(state, deviceId))
  const device = { name: deviceId, sn: vimDevice?.serial ?? '-' }

  useEffect(() => {
    dispatch(setBreadcrumb([
      { title: 'VIM List', link: '/vim-list' },
      { title: deviceId, link: `/ViewDashboard/${deviceId}` },
      { title: 'Historical Data', link: `/ViewDashboard/${deviceId}/historical`, current: true },
    ]))
  }, [dispatch, deviceId])


  // Always refetches on mount, even if this device is already in redux from
  // an earlier VIM List load — see ViewDashboard.jsx for why.
  useEffect(() => {
    if (!deviceId) return
    const fetchVimDevices = async () => {
      try {
        const response = await Api.getProfileDevices(64)
        dispatch(setVimDevices(response.data?.items ?? []))
      } catch (error) {
        console.error('Error fetching VIM devices:', error)
      }
    }
    fetchVimDevices()
  }, [deviceId, dispatch])

 
  useEffect(() => {
    let cancelled = false
    const fetchColumns = async () => {
      try {
        const { status, data } = await Api.getVimColumns()
        if (!cancelled && status === 200 && data) {
          const cols = Object.entries(data)
            .filter(([key]) => SUPPORTED_API_KEYS.has(key))
            .map(([key, rawLabel]) => {
              const { label, unit } = parseLabelUnit(rawLabel)
              return { key, label, unit: UNIT_BY_KEY[key] ?? unit, width: '190px' }
            })
          setApiColumns(cols)
          setVisibleKeys(cols.map((c) => c.key))
        }
      } catch (error) {
        console.error('Failed to fetch VIM columns:', error)
      }
    }
    fetchColumns()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!deviceId || !timeRange.from || !timeRange.to) {
      setDeviceHistory(null)
      setIsLoading(false)
      return
    }
    let cancelled = false

    const fetchDeviceHistory = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const { status, data } = await Api.getDeviceHistory(deviceId, timeRange.from.toISOString(), timeRange.to.toISOString())
        if (cancelled) return
       
        if (status !== 200) {
          console.error('Device history request failed with status', status)
          setError("Couldn't load device history")
          return
        }
        setDeviceHistory(Array.isArray(data) ? data : null)
      } catch (err) {
        console.error('Failed to fetch device history:', err)
        if (!cancelled) setError("Couldn't load device history")
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchDeviceHistory()
    return () => { cancelled = true }
  }, [deviceId, timeRange, reloadKey])


  const handleTimeRangeChange = (next) => {
    setTimeRange(next)
    setCurrentPage(1)
  }

  const isRealtime = timeRange.label === 'Last 30 min - Realtime'
  const { data: deviceUpdateData, connected } = useDeviceDashboardSocket(deviceId, isRealtime)
  const deviceTs = deviceUpdateData?.rows?.[0]?.deviceInfo?.ts

  
  const ONLINE_STALE_MS = 60 * 1000
  const lastDataAtRef = useRef(null)
  const [, forceTick] = useState(0)

  useEffect(() => {
    if (!deviceUpdateData) return
    lastDataAtRef.current = Date.now()
    forceTick((n) => n + 1)
  }, [deviceUpdateData])

  useEffect(() => {
    const interval = setInterval(() => forceTick((n) => n + 1), 5000)
    return () => clearInterval(interval)
  }, [])

  const socketHasReported = lastDataAtRef.current !== null
  const socketFresh = socketHasReported && Date.now() - lastDataAtRef.current < ONLINE_STALE_MS
  const resolvedIsOnline = socketHasReported ? (connected && socketFresh) : Boolean(vimDevice?.online)

  
  const liveSnapshotRow = useMemo(() => {
    const row0 = deviceUpdateData?.rows?.[0]
    if (!row0) return {}
    const { epochMs, timestamp, ...rest } = mapDeviceHistoryToRows([row0])[0]
    return rest
  }, [deviceUpdateData])
  const liveRows = useTelemetryHistory(deviceTs, liveSnapshotRow)
  
  const liveRowsWithTimestamp = useMemo(
    () => liveRows.map((r) => ({ ...r, timestamp: new Date(r.epochMs).toLocaleString() })),
    [liveRows],
  )

  const historyRows = useMemo(() => mapDeviceHistoryToRows(deviceHistory), [deviceHistory])
  const mappedRows = useMemo(
    () => (isRealtime ? mergeIntoWindow(historyRows, liveRowsWithTimestamp) : historyRows),
    [isRealtime, historyRows, liveRowsWithTimestamp],
  )
  const rows = useMemo(
    () => [...mappedRows].sort((a, b) => (sortDir === 'asc' ? a.epochMs - b.epochMs : b.epochMs - a.epochMs)),
    [mappedRows, sortDir],
  )
  const toggleableColumns = useMemo(
    () => apiColumns.map((c) => ({ ...c, render: ON_OFF_API_KEYS.has(c.key) ? onOffRender(c.key) : undefined })),
    [apiColumns],
  )
  const columns = [TIMESTAMP_COLUMN, ...toggleableColumns.filter((c) => visibleKeys.includes(c.key))]

  const totalItems = rows.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const start = (currentPage - 1) * itemsPerPage
  const pagedRows = rows.slice(start, start + itemsPerPage)

 
  const handleDownloadCsv = async () => {
    if (!rows.length || isExporting) return

    if (rows.length > CSV_CONFIRM_THRESHOLD) {
      const proceed = window.confirm(
        `This will export ${rows.length.toLocaleString()} rows and may take a while. Continue?`,
      )
      if (!proceed) return
    }

    setIsExporting(true)
    try {
      const blob = await buildCsvBlob({
        
        headerLine: columns.map((c) => escapeCsvCell(headerLabel(c))).join(','),
        rowCount: rows.length,
        getLine: (i) => columns.map((c) => escapeCsvCell(rows[i][c.key])).join(','),
      })
      downloadBlob(blob, `${device?.name || 'device'}-history.csv`)
    } catch (error) {
      console.error('CSV export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="w-full h-full bg-[#f2f4f7] flex flex-col">
      <div className="h-[58px] bg-[#f9fafa] flex items-center justify-between px-4 border-b border-[#dfe4ed] shrink-0 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-lg"
            
            onClick={() => navigate(`/ViewDashboard/${deviceId}`, { replace: true })}
          >
            <span className="material-icons text-xl text-[#272d37]">
              arrow_back
            </span>
          </button>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-[#272d37]">
              {device?.name || "-"}
            </span>
            <span className="text-sm font-normal text-[#616a79]">
              SN: {device?.sn || "-"}
            </span>
          </div>
          <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 ${resolvedIsOnline ? "bg-[#e6f8e6]" : "bg-[#eceded]"}`}>
            <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${resolvedIsOnline ? "bg-[#c0efc0]" : "bg-[#d5d8dc]"}`}>
              <div className={`w-2 h-2 rounded-full ${resolvedIsOnline ? "bg-[#27b83e]" : "bg-[#5e6673]"}`} />
            </div>
            <span className="text-xs font-medium text-[#272d37]">
              {resolvedIsOnline ? "ONLINE" : "OFFLINE"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TimeRangeDropdown value={timeRange.label} onChange={handleTimeRangeChange} />
          <ColumnSelector
            columns={toggleableColumns}
            visibleKeys={visibleKeys}
            onChange={setVisibleKeys}
          />
          {/* <div className="flex bg-[#dfe4ed] rounded-lg p-0.5 gap-0.5">
            <button
              className={`border-none cursor-pointer px-2.5 py-1 rounded-md flex items-center justify-center ${viewMode === 'list' ? 'bg-[#f9fafa]' : 'bg-transparent'}`}
              onClick={() => setViewMode('list')}
            >
              <span className={`material-icons text-[18px] ${viewMode === 'list' ? 'text-[#272d37]' : 'text-[#616a79]'}`}>list</span>
            </button>
            <button
              className={`border-none cursor-pointer px-2.5 py-1 rounded-md flex items-center justify-center ${viewMode === 'graph' ? 'bg-[#f9fafa]' : 'bg-transparent'}`}
              onClick={() => setViewMode('graph')}
            >
              <span className={`material-icons text-[18px] ${viewMode === 'graph' ? 'text-[#272d37]' : 'text-[#616a79]'}`}>show_chart</span>
            </button>
          </div> */}
          <button className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-lg disabled:opacity-50"
            onClick={handleDownloadCsv}
            disabled={!rows.length || isExporting}
            title={isExporting ? 'Exporting…' : 'Export CSV'}
          >
            <span className="material-symbols-outlined text-xl text-[#272d37]">
              {isExporting ? 'hourglass_top' : 'file_save'}
            </span>
          </button>
        </div>
      </div>

      {/* Loading/error live inside CustomTable rather than replacing the whole
          view: the realtime range refetches constantly, and swapping the table
          out each time made the header and columns flicker. */}
      {viewMode === "list" ? (
        <CustomTable
          columns={columns}
          data={pagedRows}
          isLoading={isLoading}
          error={error}
          onRetry={() => setReloadKey((n) => n + 1)}
          loadingLabel="Loading device history…"
          emptyMessage="No data in this time range"
          headerBg="#f2f4f7"
          textColor="#272d37"
          rowBorderColor="#f2f4f7"
          stickyHeader
          fitColumnsToContent
          sortColumn="timestamp"
          sortDirection={sortDir}
          onSort={() => {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"))
            setCurrentPage(1)
          }}
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage,
            onPageChange: setCurrentPage,
            onItemsPerPageChange: setItemsPerPage,
          }}
        />
      ) : (
        <div className="flex-1 flex items-center justify-center text-[#616a79] text-sm gap-2">
          <span className="material-icons text-xl">show_chart</span>
          Chart view coming soon
        </div>
      )}
    </div>
  );
}

export default HistoricalData
